import {
  DispositivoDto,
  generateReportProprietaMisurata,
  GetDispositivoHistoryDataParams,
  getGetDispositivoHistoryDataQueryOptions,
  ProprietaMisurataDto,
  TrasferimentoDto,
} from "@/api";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import { getDispositivoTypeLabel } from "@/utils/dispositivoUtils";
import { getProprietaMisurataHelper } from "@/utils/proprietaMisurataUtils";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { DateTimeField } from "@mui/x-date-pickers";
import { useQueries } from "@tanstack/react-query";
import moment, { Moment } from "moment";
import { useCallback, useMemo, useState } from "react";
import Loading from "../Loading";
import LoadingError from "../LoadingError";
import ProprietaMisurataHistoryChart, {
  ChartData,
} from "../ProprietaMisurataHistoryChart";
import HistoryIntervalSlider from "./HistoryIntervalSlider";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";

type ProprietaMisurataIntervalsHelper = {
  name: string;
  getFrom: () => Moment;
};

const ProprietaMisurataIntervals: {
  [label: string]: ProprietaMisurataIntervalsHelper;
} = {
  "1h": {
    name: "1h",
    getFrom: () => moment().subtract(1, "h"),
  },
  "3h": {
    name: "3h",
    getFrom: () => moment().subtract(3, "h"),
  },
  "6h": {
    name: "6h",
    getFrom: () => moment().subtract(6, "h"),
  },
  "12h": {
    name: "12h",
    getFrom: () => moment().subtract(12, "h"),
  },
  "24h": {
    name: "24h",
    getFrom: () => moment().subtract(24, "h"),
  },
  "2d": {
    name: "2d",
    getFrom: () => moment().subtract(2, "days"),
  },
  "7d": {
    name: "7d",
    getFrom: () => moment().subtract(7, "days"),
  },
  "30d": {
    name: "30d",
    getFrom: () => moment().subtract(30, "days"),
  },
};

type RangeOrIntervalType =
  | {
      type: "custom";
      key: string;
      from?: Moment | null;
      to?: Moment | null;
    }
  | {
      type: "interval";
      key: string;
      helper: ProprietaMisurataIntervalsHelper;
    }
  | {
      type: "trasferimento";
      key: string;
      trasferimento: TrasferimentoDto;
    };

function ProprietaMisurataHistoryDialog({
  open = false,
  onClose,
  dispositivo,
  proprietaMisurateInitial,
}: {
  open?: boolean;
  onClose?: () => void;
  dispositivo?: DispositivoDto;
  proprietaMisurateInitial?: ProprietaMisurataDto[];
}) {
  const [intervalOrRange, setIntervalOrRange] = useState<RangeOrIntervalType>({
    type: "interval",
    key: "24h",
    helper: ProprietaMisurataIntervals["24h"],
  });
  const [proprietaMisurate, setProprietaMisurate] = useState(
    proprietaMisurateInitial ?? []
  );

  const [rangeZoom, setRangeZoom] = useState<[string, string] | [null, null]>([
    null,
    null,
  ]);

  const selectedTrasferimentoObject = useMemo(() => {
    if (intervalOrRange?.type !== "trasferimento") {
      return null;
    }

    return (
      dispositivo?.trasferimenti?.find(
        (t) => t.id === intervalOrRange?.trasferimento?.id
      ) ?? null
    );
  }, [intervalOrRange, dispositivo]);

  const baseParams = useMemo(() => {
    const params: GetDispositivoHistoryDataParams = {};
    if (intervalOrRange.type === "interval") {
      params._from = intervalOrRange.helper.getFrom().toISOString();
      params._to = moment().toISOString();
    } else if (intervalOrRange.type === "trasferimento") {
      params.idTrasferimento = intervalOrRange.trasferimento.id;
    } else {
      if (intervalOrRange.from && intervalOrRange.to) {
        params._from = intervalOrRange.from?.toISOString();
        params._to = intervalOrRange.to?.toISOString();
      } else {
        return null;
      }
    }
    return params;
  }, [intervalOrRange]);

  const canQueryData = Boolean(open && dispositivo && baseParams);
  const queriesResults = useQueries({
    queries: proprietaMisurate.map((p) => {
      return getGetDispositivoHistoryDataQueryOptions(
        dispositivo?.id ?? 0,
        p.id,
        {
          _from: baseParams?._from,
          _to: baseParams?._to,
          idTrasferimento: baseParams?.idTrasferimento,
        },
        {
          query: {
            enabled: canQueryData,
          },
        }
      );
    }),
  });

  const [isLoading, error, chartData] = useMemo(() => {
    const filterZoom = rangeZoom[0] && rangeZoom[1];
    const from = moment(rangeZoom[0]);
    const to = moment(rangeZoom[1]);

    let atLeastOneLoading = false;
    let atLeastOneError: any | null = null;

    const data: ChartData[] = [];
    queriesResults.forEach((result, index) => {
      if (result.isLoading) {
        if (result.fetchStatus === "fetching") {
          atLeastOneLoading = true;
        }
      } else if (result.error) {
        if (!atLeastOneError) {
          atLeastOneError = result.error;
        }
      } else {
        const proprietaMisurata = proprietaMisurate[index];
        const d: ChartData = {
          proprietaMisurata: proprietaMisurata,
          values: filterZoom
            ? result.data.data.filter((x) => {
                const m = moment(x.timestamp);
                return m.isSameOrAfter(from) && m.isSameOrBefore(to);
              })
            : result.data.data,
        };
        data.push(d);
      }
    });

    return [atLeastOneLoading, atLeastOneError, data] as const;
  }, [queriesResults, rangeZoom, proprietaMisurate]);

  const handleChange = useCallback(
    (value: string, from?: Moment | null, to?: Moment | null) => {
      if (ProprietaMisurataIntervals[value]) {
        setIntervalOrRange({
          type: "interval",
          key: value,
          helper: ProprietaMisurataIntervals[value],
        });
      } else if (value.startsWith("t-")) {
        const id = parseInt(value.split("-")[1]);
        const trasferimento = dispositivo?.trasferimenti?.find(
          (t) => t.id === id
        );
        if (trasferimento) {
          setIntervalOrRange({
            type: "trasferimento",
            key: value,
            trasferimento: trasferimento,
          });
        }
      } else if (value === "custom") {
        setIntervalOrRange({
          type: "custom",
          key: value,
          from: from ?? null,
          to: to ?? null,
        });
      }

      setRangeZoom([null, null]);
    },
    [dispositivo]
  );

  const dispositivoLabel = dispositivo
    ? `${getDispositivoTypeLabel(dispositivo.type)} ${dispositivo.matricola}`
    : "";

  const handleClose = () => {
    onClose?.();
  };

  const canExportPdf = canQueryData && proprietaMisurate.length > 0;
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const handleExportPdf = () => {
    if (!canExportPdf || !dispositivo) {
      return;
    }

    setIsExportingPdf(true);
    generateReportProprietaMisurata(dispositivo.id, {
      proprietaMisurateIds: proprietaMisurate.map((p) => p.id),
      from: baseParams?._from,
      to: baseParams?._to,
      trasferimentoId: baseParams?.idTrasferimento,
    })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const urlBlob = window.URL.createObjectURL(blob);

        // extract file name
        const contentDisposition = response.headers["content-disposition"];
        let fileName = "report.pdf";

        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            fileName = match[1];
          }
        }

        const link = document.createElement("a");
        link.href = urlBlob;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("PDF generato con successo!");
      })
      .catch(() => {
        toast.error(
          "Si è verificato un errore durante la generazione del PDF. Riprovare più tardi"
        );
      })
      .finally(() => {
        setIsExportingPdf(false);
      });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <strong>Storico {dispositivoLabel}</strong>
      </DialogTitle>
      <DialogContent>
        <Box display="grid" gridTemplateColumns="auto 1fr" gap={1}>
          <Box>
            <Typography sx={{ fontSize: "14px" }}>
              <strong>Periodo:</strong>
            </Typography>
          </Box>
          <Box>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              gap={1}
            >
              <Box>
                <FormControl
                  sx={{
                    minWidth:
                      intervalOrRange.type === "trasferimento" ? 400 : 120,
                  }}
                  size="small"
                >
                  <Select
                    value={intervalOrRange.key}
                    onChange={(e) => handleChange(e.target.value)}
                    disabled={isExportingPdf}
                  >
                    {Object.keys(ProprietaMisurataIntervals).map((key) => {
                      return (
                        <MenuItem key={key} value={key}>
                          {key}
                        </MenuItem>
                      );
                    })}
                    {dispositivo?.trasferimenti &&
                      dispositivo.trasferimenti.length >= 1 && <Divider />}

                    {(dispositivo?.trasferimenti || []).map((t) => {
                      const value = `t-${t.id}`;
                      const key = value;

                      const labelDa = t.da
                        ? `Da ${moment(t.da).format("YYYY-MM-DD")}`
                        : "";
                      const labelA = t.a
                        ? `A ${moment(t.a).format("YYYY-MM-DD")}`
                        : "";
                      const label = [labelDa, labelA]
                        .filter((x) => x)
                        .join(" - ");

                      return (
                        <MenuItem key={key} value={key}>
                          <div>
                            {label}
                            <br />
                            <i>{t.azienda?.nome || "-"}</i>
                          </div>
                        </MenuItem>
                      );
                    })}

                    <Divider />
                    <MenuItem value="custom">Personalizzato</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {intervalOrRange.type === "custom" && (
                <>
                  <Typography sx={{ fontSize: "14px" }}>Dal</Typography>
                  <DateTimeField
                    format="DD/MM/YYYY HH:mm"
                    onChange={(value) =>
                      handleChange("custom", value, intervalOrRange.to)
                    }
                    value={intervalOrRange.from}
                    disabled={isExportingPdf}
                  />
                  <Typography sx={{ fontSize: "14px" }}>Al</Typography>
                  <DateTimeField
                    format="DD/MM/YYYY HH:mm"
                    onChange={(value) =>
                      handleChange("custom", intervalOrRange.from, value)
                    }
                    value={intervalOrRange.to}
                    disabled={isExportingPdf}
                  />
                </>
              )}
            </Box>
          </Box>
          <Box sx={{ maxWidth: 240 }}>
            <ProprietaMisurataPicker
              proprietaMisurate={dispositivo?.proprietaMisurate ?? []}
              value={proprietaMisurate}
              onChange={setProprietaMisurate}
              disabled={isExportingPdf}
            />
          </Box>
          <Box>
            <Box display="flex" flexDirection="column" flex={1} gap={1}>
              <Box
                flex={1}
                sx={{
                  border: "1px solid #d2d6da",
                  borderRadius: "8px",
                }}
              >
                <Box display="flex" flex={1}>
                  {selectedTrasferimentoObject &&
                  selectedTrasferimentoObject.da &&
                  selectedTrasferimentoObject.a ? (
                    <HistoryIntervalSlider
                      fromDate={selectedTrasferimentoObject.da}
                      toDate={selectedTrasferimentoObject.a}
                      value={[
                        rangeZoom[0] || selectedTrasferimentoObject.da,
                        rangeZoom[1] || selectedTrasferimentoObject.a,
                      ]}
                      onChange={([from, to]) => {
                        console.log(from, to);
                        setRangeZoom([from, to]);
                      }}
                    />
                  ) : null}
                </Box>

                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ height: 500, position: "relative" }}
                >
                  {isLoading && (
                    <Box
                      sx={{
                        position: "absolute",
                        flex: 1,
                        backgroundColor: "rgba(0,0,0,0.05)",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Loading />
                    </Box>
                  )}
                  {!baseParams || proprietaMisurate.length <= 0 ? (
                    <Typography variant="body2">
                      <em>
                        Seleziona almeno una misura ed un intervallo valido per
                        generare il grafico
                      </em>
                    </Typography>
                  ) : error ? (
                    <LoadingError error={error} />
                  ) : (
                    <ProprietaMisurataHistoryChart
                      height={500}
                      data={chartData}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          size="small"
          color="light"
          onClick={handleExportPdf}
          disabled={!canExportPdf || isExportingPdf}
          startIcon={
            isExportingPdf ? <CircularProgress size={20} /> : <DownloadIcon />
          }
        >
          Esporta PDF
        </Button>

        {onClose && (
          <Button
            size="small"
            color="light"
            onClick={handleClose}
            disabled={isExportingPdf}
          >
            Chiudi
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ProprietaMisurataPicker({
  proprietaMisurate,
  value,
  onChange,
  disabled: _disabled = false,
}: {
  proprietaMisurate: ProprietaMisurataDto[];
  value: ProprietaMisurataDto[];
  onChange: (newValues: ProprietaMisurataDto[]) => void;
  disabled?: boolean;
}) {
  const units = useMemo(() => {
    const units: string[] = [];
    for (const v of value) {
      const helper = getProprietaMisurataHelper(v.type);
      if (helper) {
        const unit = helper.unit ?? "scalar";
        if (!units.includes(unit)) {
          units.push(unit);
        }
      }
    }
    return units;
  }, [value]);

  let atLeastOneDisabled = false;

  return (
    <FormControl component="fieldset" variant="standard">
      <FormLabel component="legend">Misure:</FormLabel>
      <FormGroup>
        {proprietaMisurate
          .filter((p) => p.type !== "unknown")
          .map((p) => {
            const checked = !!value.find((checkedP) => checkedP.id === p.id);
            const helper = getProprietaMisurataHelper(p.type);
            const unit = helper?.unit ?? "scalar";
            const disabled = !units.includes(unit) && units.length >= 2;
            if (disabled) {
              atLeastOneDisabled = true;
            }

            return (
              <FormControlLabel
                key={p.id}
                disabled={_disabled || disabled}
                control={
                  <Checkbox
                    checked={checked}
                    sx={{ ml: 1.5 }}
                    onChange={() => {
                      if (checked) {
                        onChange(
                          value.filter((checkedP) => checkedP.id !== p.id)
                        );
                      } else {
                        onChange([...value, p]);
                      }
                    }}
                  />
                }
                label={
                  helper
                    ? `${helper.label} ${
                        helper.unit ? "(" + helper.unit + ")" : ""
                      }`
                    : p.type
                }
              />
            );
          })}
      </FormGroup>
      {atLeastOneDisabled && (
        <FormHelperText>
          Puoi selezinare più proprietà, ammesso che abbiano al massimo due
          unità di misura diverse
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default withOpeningReset(ProprietaMisurataHistoryDialog);
